import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { MultiSelect } from 'primereact/multiselect';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { formatDateOnly, formatDateWithTime } from '../../../../../utils/dateUtils';
import { useGetPreStockInspectionHistoryQuery } from '../../../../../api';

const PreStockInspectionHistoryDialog = ({ hideDialog, dialogVisible, preStockInspection }) => {
    const { th, getCurrentLanguageId } = useLanguage();

    const [historyData, setHistoryData] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState([
        'inspectorName',
        'inspectorTitle',
        'vin',
        'inspectionDate',
        'stockInDate',
        'mileageKm',
        'model',
        'modelYear',
        'notesOrOtherDamages',
        'status',
        'createdTimestamp',
        'modifiedTimestamp',
        'createdName',
        'modifiedName'
    ]);
    // Reference for DataTable export
    const dt = useRef(null);

    const shouldFetch = dialogVisible && preStockInspection && preStockInspection.id;

    const {
        data: historyRecords,
        isLoading,
        isError,
        error
    } = useGetPreStockInspectionHistoryQuery(
        {
            id: preStockInspection?.id,
            pageSize: 50,
            pageNumber: 0,
            languageId: getCurrentLanguageId()
        },
        {
            skip: !shouldFetch,
            refetchOnMountOrArgChange: true
        }
    );

    useEffect(() => {
        if (historyRecords && historyRecords.items) {
            setHistoryData(historyRecords.items);
        } else {
            setHistoryData([]);
        }
    }, [historyRecords]);

    const columnConfigs = [
        {
            field: 'inspectorName',
            header: th('warranty.preStockInspection.history.inspectorName'),
            sortable: true,
            style: { minWidth: '150px' }
        },
        {
            field: 'inspectorTitle',
            header: th('warranty.preStockInspection.history.inspectorTitle'),
            sortable: true,
            style: { minWidth: '150px' }
        },
        {
            field: 'vin',
            header: th('warranty.preStockInspection.history.vin'),
            sortable: true,
            style: { minWidth: '180px' }
        },
        {
            field: 'inspectionDate',
            header: th('warranty.preStockInspection.history.inspectionDate'),
            body: (rowData) => formatDateOnly(rowData.inspectionDate),
            sortable: true,
            style: { minWidth: '150px' }
        },
        {
            field: 'stockInDate',
            header: th('warranty.preStockInspection.history.stockInDate'),
            body: (rowData) => formatDateOnly(rowData.stockInDate),
            sortable: true,
            style: { minWidth: '150px' }
        },
        {
            field: 'mileageKm',
            header: th('warranty.preStockInspection.history.mileageKm'),
            body: (rowData) => (rowData.mileageKm ? rowData.mileageKm.toLocaleString('tr-TR') + ' km' : '0 km'),
            sortable: true,
            style: { minWidth: '120px' }
        },
        {
            field: 'model',
            header: th('warranty.preStockInspection.history.model'),
            sortable: true,
            style: { minWidth: '150px' }
        },
        {
            field: 'modelYear',
            header: th('warranty.preStockInspection.history.modelYear'),
            sortable: true,
            style: { minWidth: '100px' }
        },
        {
            field: 'notesOrOtherDamages',
            header: th('warranty.preStockInspection.history.notesOrOtherDamages'),
            sortable: true,
            style: { minWidth: '200px' }
        },
        {
            field: 'status',
            header: th('warranty.preStockInspection.history.status'),
            sortable: true,
            style: { minWidth: '120px' }
        },
        {
            field: 'createdTimestamp',
            header: th('warranty.preStockInspection.history.createdTimestamp'),
            body: (rowData) => formatDateWithTime(rowData.createdTimestamp),
            sortable: true,
            style: { minWidth: '180px' }
        },
        {
            field: 'modifiedTimestamp',
            header: th('warranty.preStockInspection.history.modifiedTimestamp'),
            body: (rowData) => formatDateWithTime(rowData.modifiedTimestamp),
            sortable: true,
            style: { minWidth: '180px' }
        },
        {
            field: 'createdName',
            header: th('warranty.preStockInspection.history.createdName'),
            sortable: true,
            style: { minWidth: '150px' }
        },
        {
            field: 'modifiedName',
            header: th('warranty.preStockInspection.history.modifiedName'),
            sortable: true,
            style: { minWidth: '150px' }
        }
    ];

    const columnOptions = columnConfigs.map((config) => ({
        label: config.header,
        value: config.field
    }));

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const headerTemplate = () => {
        return (
            <div className="flex flex-column sm:flex-row w-full justify-content-end align-items-stretch sm:align-items-center gap-2">
                <div className="w-full sm:w-auto">
                    <MultiSelect
                        value={visibleColumns}
                        options={columnOptions}
                        onChange={(e) => setVisibleColumns(e.value)}
                        className="w-full"
                        placeholder={th('warranty.preStockInspection.history.selectColumns')}
                        maxSelectedLabels={3}
                        selectedItemsLabel={th('warranty.preStockInspection.history.selectedItems')}
                        panelStyle={{ width: '300px' }}
                    />
                </div>
                <Button type="button" icon="pi pi-file" label={th('warranty.preStockInspection.history.csvExport')} onClick={exportCSV} className="p-button-outlined w-full sm:w-auto" disabled={!historyData || historyData.length === 0} />
            </div>
        );
    };

    const renderDialogFooter = () => {
        return (
            <div className="flex justify-content-end gap-2">
                <Button label={th('warranty.preStockInspection.history.close')} icon="pi pi-times" outlined onClick={hideDialog} type="button" />
            </div>
        );
    };

    return (
        <Dialog
            visible={dialogVisible}
            style={{ width: '90%', maxWidth: '1200px' }}
            header={`${th('warranty.preStockInspection.history.title')} - ${preStockInspection?.vin || ''}`}
            modal
            className="p-fluid"
            footer={renderDialogFooter()}
            onHide={hideDialog}
            maximizable
        >
            {isLoading && (
                <div className="flex justify-content-center">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                </div>
            )}

            {isError && <div className="p-error">{th('warranty.preStockInspection.history.loadingError').replace('{0}', error?.message || th('warranty.preStockInspection.history.unknownError'))}</div>}

            {!isLoading && !isError && (!historyData || historyData.length === 0) && <div className="text-center p-4">{th('warranty.preStockInspection.history.noHistoryFound')}</div>}

            {!isLoading && !isError && historyData && historyData.length > 0 && (
                <DataTable
                    ref={dt}
                    value={historyData}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    className="p-datatable-gridlines"
                    showGridlines
                    stripedRows
                    emptyMessage={th('warranty.preStockInspection.history.emptyMessage')}
                    dataKey="id"
                    tableStyle={{ minWidth: '50rem' }}
                    header={headerTemplate()}
                >
                    {columnConfigs
                        .filter((config) => visibleColumns.includes(config.field))
                        .map((config) => (
                            <Column key={config.field} field={config.field} header={config.header} body={config.body} sortable={config.sortable} style={config.style} />
                        ))}
                </DataTable>
            )}
        </Dialog>
    );
};

export default PreStockInspectionHistoryDialog;
